/**
 * ZHVI per-zip lookup — `GET /admin/data/zhvi/zip/{zip}` → `{ record, assessment }`.
 *
 * Renders exactly what admin-market-data-zhvi.md specifies:
 *   • the market card off the MarketAssessment (median value, MoM/YoY, 3m/6m
 *     annualized, trend chip, metro / rural signal, subject multiplier)
 *   • the 24-month `record.timeSeries` as a chart
 *   • the `guidelineFlags` — rows carrying a `reduction` are leverage reductions
 *     (pricing inputs); the rest are approval/review items
 *
 * Trend chip colors are the documented ones: increasing=green, stable=neutral,
 * decreasing=amber, strongly_declining=red (that one always carries a soft
 * exception flag).
 */
import { useCallback, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import SearchRounded from "@mui/icons-material/SearchRounded";
import { useSnackbar } from "notistack";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { adminZhviApi } from "../../../api/adminDataApi";
import formatDollar from "../../../_src_shared/utils/functions/formatDollar";
import { formatDate } from "../../../_src_shared/utils/functions/formatDate";
import { AdminCard, AdminEmpty } from "../shared/AdminScreenShell";

const TREND_STYLES = {
  increasing: { bg: "#dcfce7", fg: "#166534", label: "Increasing" },
  stable: { bg: "#f1f5f9", fg: "#475569", label: "Stable" },
  decreasing: { bg: "#fef3c7", fg: "#92400e", label: "Decreasing" },
  strongly_declining: { bg: "#fee2e2", fg: "#991b1b", label: "Strongly declining" },
  unknown: { bg: "#f1f5f9", fg: "#94a3b8", label: "Unknown" },
};

// HPA values arrive as fractions (-0.0321 = -3.21%).
const pct = (v) =>
  v == null || Number.isNaN(Number(v))
    ? "—"
    : `${Number(v) >= 0 ? "+" : ""}${(Number(v) * 100).toFixed(2)}%`;

const hpaColor = (v) =>
  v == null ? "#0f172a" : Number(v) < 0 ? "#b91c1c" : "#166534";

/**
 * Inline SVG sparkline of the 24-month series. Deliberately dependency-free —
 * the payload is a fixed 24 points and a chart library would be overkill here.
 */
const TimeSeriesChart = ({ points }) => {
  const path = useMemo(() => {
    const vals = (points || [])
      .map((p) => Number(p?.value))
      .filter((v) => Number.isFinite(v));
    if (vals.length < 2) return null;
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const span = max - min || 1;
    const W = 100;
    const H = 32;
    const d = vals
      .map((v, i) => {
        const x = (i / (vals.length - 1)) * W;
        const y = H - ((v - min) / span) * H;
        return `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(" ");
    return { d, min, max, first: vals[0], last: vals[vals.length - 1] };
  }, [points]);

  if (!path) return <AdminEmpty>No time series for this zip.</AdminEmpty>;

  const rising = path.last >= path.first;
  const stroke = rising ? "#16a34a" : "#dc2626";

  return (
    <Box>
      <Box
        component="svg"
        viewBox="0 0 100 32"
        preserveAspectRatio="none"
        sx={{ width: "100%", height: 120, display: "block" }}
      >
        <path d={path.d} fill="none" stroke={stroke} strokeWidth={0.8} />
      </Box>
      <Flx jb sx={{ mt: 0.5 }}>
        <Txt sx={{ fontSize: 11, color: "#94a3b8" }}>
          {formatDollar(Math.round(path.min), "—")} low
        </Txt>
        <Txt sx={{ fontSize: 11, color: "#94a3b8" }}>
          {formatDollar(Math.round(path.max), "—")} high
        </Txt>
      </Flx>
    </Box>
  );
};

export default function ZhviZipPanel() {
  const { enqueueSnackbar } = useSnackbar();
  const [zip, setZip] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const lookup = useCallback(() => {
    const z = zip.trim();
    if (!z) return;
    setBusy(true);
    adminZhviApi
      .zip(z)
      .then((res) => setData(res))
      .catch((err) => {
        setData(null);
        enqueueSnackbar(
          err?.response?.status === 404
            ? `No ZHVI data for zip ${z} in the active dataset`
            : err?.response?.data?.detail || "Zip lookup failed",
          { variant: err?.response?.status === 404 ? "warning" : "error" },
        );
      })
      .finally(() => setBusy(false));
  }, [zip, enqueueSnackbar]);

  const record = data?.record;
  const a = data?.assessment;
  const trend = TREND_STYLES[a?.trend] || TREND_STYLES.unknown;

  return (
    <AdminCard
      title="Zip lookup"
      endContent={
        <Flx ac g={1}>
          <TextField
            size="small"
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && lookup()}
            placeholder="78701"
            inputProps={{ maxLength: 10 }}
            sx={{ width: 130 }}
          />
          <Button
            variant="outlined"
            startIcon={<SearchRounded />}
            onClick={lookup}
            disabled={busy || !zip.trim()}
          >
            Look up
          </Button>
        </Flx>
      }
    >
      {!data ? (
        <AdminEmpty>
          Enter a zip to see the record and market assessment the opening agent
          reads — median value, HPA, trend, metro / rural signal and the
          guideline flags it stages findings from.
        </AdminEmpty>
      ) : (
        <Flx column g={2}>
          <Flx g={4} wrap ac>
            <Flx column g={0.25}>
              <Txt sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>
                Median home value {a?.asOfDate ? `· ${formatDate(a.asOfDate)}` : ""}
              </Txt>
              <Txt sx={{ fontSize: 24, fontWeight: 800, color: "#0f172a" }}>
                {formatDollar(a?.medianValue ?? record?.latestZHVI, "—")}
              </Txt>
            </Flx>
            <Metric label="HPA MoM" value={pct(a?.hpaMoM ?? record?.hpaMoM)} color={hpaColor(a?.hpaMoM ?? record?.hpaMoM)} />
            <Metric label="HPA YoY" value={pct(a?.hpaYoY ?? record?.hpaYoY)} color={hpaColor(a?.hpaYoY ?? record?.hpaYoY)} />
            <Metric label="3m annualized" value={pct(a?.hpa3mAnnualized)} color={hpaColor(a?.hpa3mAnnualized)} />
            <Metric label="6m annualized" value={pct(a?.hpa6mAnnualized)} color={hpaColor(a?.hpa6mAnnualized)} />
            <Flx column g={0.5}>
              <Txt sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>
                Trend
              </Txt>
              <Chip
                size="small"
                label={trend.label}
                sx={{
                  bgcolor: trend.bg,
                  color: trend.fg,
                  fontWeight: 700,
                  fontSize: 11,
                  height: 22,
                }}
              />
            </Flx>
          </Flx>

          <Flx g={4} wrap>
            <Metric
              label="Metro"
              value={a?.metro || record?.metro || "—"}
              // "No Metro - check rural" is the documented unmapped marker.
              color={a?.metroStatus === "found" ? "#0f172a" : "#b45309"}
            />
            <Metric label="County" value={a?.countyName || record?.countyName || "—"} />
            <Metric
              label="Rural signal"
              value={a?.ruralSignal ? "Yes" : "No"}
              color={a?.ruralSignal ? "#b45309" : "#0f172a"}
            />
            <Metric
              label="CFPB rural county"
              value={a?.cfpbRuralCounty ? "Yes" : "No"}
              color={a?.cfpbRuralCounty ? "#b45309" : "#0f172a"}
            />
            {a?.zillowMultiplier != null ? (
              <Metric
                label="Subject / ZHVI multiplier"
                value={`${Number(a.zillowMultiplier).toFixed(2)}x`}
                color={Number(a.zillowMultiplier) > 3 ? "#b91c1c" : "#0f172a"}
              />
            ) : null}
          </Flx>

          <Box>
            <Txt
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: "#0f172a",
                display: "block",
                mb: 1,
              }}
            >
              24-month ZHVI
            </Txt>
            <TimeSeriesChart points={record?.timeSeries} />
          </Box>

          {a?.guidelineFlags?.length ? (
            <Box>
              <Txt
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#0f172a",
                  display: "block",
                  mb: 1,
                }}
              >
                Guideline flags ({a.guidelineFlags.length})
              </Txt>
              <Flx column g={1}>
                {a.guidelineFlags.map((f, i) => (
                  <Flx
                    key={`${f.category}-${i}`}
                    ac
                    g={1}
                    sx={{
                      p: 1.25,
                      borderRadius: "8px",
                      border: "1px solid #e5e7eb",
                      bgcolor: f.reduction != null ? "#fffbeb" : "#f8fafc",
                    }}
                    wrap
                  >
                    <Chip
                      size="small"
                      label={f.category || "flag"}
                      sx={{ fontSize: 11, height: 20, fontWeight: 600 }}
                    />
                    <Txt sx={{ fontSize: 12.5, color: "#0f172a", flex: 1 }}>
                      {f.reason}
                    </Txt>
                    {f.exception_level ? (
                      <Txt sx={{ fontSize: 11.5, color: "#64748b" }}>
                        {f.exception_level}
                      </Txt>
                    ) : null}
                    {f.reduction != null ? (
                      <Chip
                        size="small"
                        label={`leverage −${(Number(f.reduction) * 100).toFixed(0)}%`}
                        sx={{
                          bgcolor: "#fef3c7",
                          color: "#92400e",
                          fontWeight: 700,
                          fontSize: 11,
                          height: 20,
                        }}
                      />
                    ) : null}
                  </Flx>
                ))}
              </Flx>
            </Box>
          ) : null}
        </Flx>
      )}
    </AdminCard>
  );
}

const Metric = ({ label, value, color = "#0f172a" }) => (
  <Flx column g={0.25}>
    <Txt sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>{label}</Txt>
    <Txt sx={{ fontSize: 14, fontWeight: 700, color }}>{value}</Txt>
  </Flx>
);
